/**
 * The whole icon set, drawn inline. It is small on purpose: no icon font and
 * no icon dependency, so the site stays one bundle on the venue's slow
 * connection. An icon never appears without a text label except the overflow
 * `⋮`, which carries an `aria-label` naming its row.
 */
import type { ComponentChildren } from "preact";

interface GlyphProps {
  children: ComponentChildren;
  /** Filled glyphs (the Google mark) draw their own paint instead. */
  painted?: boolean;
}

function Glyph({ children, painted }: GlyphProps) {
  return (
    <svg
      class="icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={painted ? "none" : "currentColor"}
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function ChevronRight() {
  return (
    <Glyph>
      <path d="M9 5l7 7-7 7" />
    </Glyph>
  );
}

export function ChevronLeft() {
  return (
    <Glyph>
      <path d="M15 5l-7 7 7 7" />
    </Glyph>
  );
}

export function Plus() {
  return (
    <Glyph>
      <path d="M12 5v14M5 12h14" />
    </Glyph>
  );
}

export function Microphone() {
  return (
    <Glyph>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6" />
    </Glyph>
  );
}

export function Keyboard() {
  return (
    <Glyph>
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <path d="M6.5 10h.01M10 10h.01M13.5 10h.01M17 10h.01M8 14h8" />
    </Glyph>
  );
}

export function Play() {
  return (
    <Glyph>
      <path d="M7 4.5l12 7.5-12 7.5z" />
    </Glyph>
  );
}

export function StopSquare() {
  return (
    <Glyph>
      <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
    </Glyph>
  );
}

export function MoreVertical() {
  return (
    <Glyph painted>
      <circle cx="12" cy="5.5" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="18.5" r="1.6" fill="currentColor" />
    </Glyph>
  );
}

/**
 * The teeth are a computed outline, not a path written by hand — a gear drawn
 * from memory shipped once with a tooth missing. Eight teeth around a hub, tip
 * radius 8.7 and root 6.1 about the 24-unit box's centre.
 */
export function Gear() {
  return (
    <Glyph>
      <path d="M10.19 3.49 L13.81 3.49 L13.68 6.14 L14.96 6.66 L16.74 4.7 L19.3 7.26 L17.34 9.04 L17.86 10.32 L20.51 10.19 L20.51 13.81 L17.86 13.68 L17.34 14.96 L19.3 16.74 L16.74 19.3 L14.96 17.34 L13.68 17.86 L13.81 20.51 L10.19 20.51 L10.32 17.86 L9.04 17.34 L7.26 19.3 L4.7 16.74 L6.66 14.96 L6.14 13.68 L3.49 13.81 L3.49 10.19 L6.14 10.32 L6.66 9.04 L4.7 7.26 L7.26 4.7 L9.04 6.66 L10.32 6.14 Z" />
      <circle cx="12" cy="12" r="3.1" />
    </Glyph>
  );
}

export function Lock() {
  return (
    <Glyph>
      <rect x="4.5" y="10" width="15" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </Glyph>
  );
}

export function Check() {
  return (
    <Glyph>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </Glyph>
  );
}

export function Info() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 7.8h.01" />
    </Glyph>
  );
}

export function Close() {
  return (
    <Glyph>
      <path d="M6 6l12 12M18 6L6 18" />
    </Glyph>
  );
}

export function Clock() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.2 2" />
    </Glyph>
  );
}

/** Google's own mark, so the sign-in button is the one people expect. */
export function GoogleMark() {
  return (
    <Glyph painted>
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.71-.06-1.4-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.89-1.74 2.99-4.3 2.99-7.35z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.96-.9 6.61-2.42l-3.23-2.5c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H3.07v2.58A10 10 0 0 0 12 22z"
      />
      <path
        fill="#FBBC05"
        d="M6.41 13.92a6 6 0 0 1 0-3.83V7.5H3.07a10 10 0 0 0 0 9l3.34-2.58z"
      />
      <path
        fill="#EA4335"
        d="M12 5.96c1.47 0 2.79.5 3.83 1.5l2.86-2.86C16.95 2.99 14.7 2 12 2a10 10 0 0 0-8.93 5.5l3.34 2.59C7.2 7.72 9.4 5.96 12 5.96z"
      />
    </Glyph>
  );
}
